import type { AdminPasswordResetResult, AdminUserCreateInput, AdminUserRow, AdminUserUpdateInput, AuditEntry, AuthState, EnterpriseLogEntry, EnterpriseLogPage, EnterpriseLogQuery, FamilyMemberRole, FamilyMemberSummary, HealthStatus, LoggingSettings, SessionInfo, WebsiteAsset, WebsiteNavigationItem, WebsiteOverview, WebsitePage, WebsitePageSummary, WebsitePayload, WebsiteSettings } from './types';

const API_BASE_URL = (import.meta.env.VITE_SIKTRA_API_BASE_URL || 'https://api.siktra-sleep.com').replace(/\/$/, '');
const TOKEN_STORAGE_KEY = 'siktra_manage_access_token';
const DEFAULT_LOGGING_SETTINGS: LoggingSettings = { mode: 'standard', debugUntil: null };

export class SiktraApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code = 'API_ERROR') {
    super(message);
    this.name = 'SiktraApiError';
    this.status = status;
    this.code = code;
  }
}

function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'ok' in payload && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function extractError(payload: unknown, fallback: string): { message: string; code: string } {
  if (payload && typeof payload === 'object') {
    const object = payload as { error?: { message?: string; code?: string }; message?: string; code?: string };
    return {
      message: object.error?.message ?? object.message ?? fallback,
      code: object.error?.code ?? object.code ?? 'API_ERROR',
    };
  }
  return { message: fallback, code: 'API_ERROR' };
}

function isLoggingSettings(value: unknown): value is LoggingSettings {
  if (!value || typeof value !== 'object') return false;
  const mode = (value as { mode?: unknown }).mode;
  return mode === 'off' || mode === 'standard' || mode === 'extended' || mode === 'debug';
}

function normalizeEnterpriseLogEntry(value: unknown): EnterpriseLogEntry {
  const object = value && typeof value === 'object' ? value as EnterpriseLogEntry : { id: crypto.randomUUID(), level: 'info', source: 'unknown' };
  const rawMetadata = object.metadata ?? object.metadataJson ?? object.metadata_json ?? null;
  let metadata: unknown = rawMetadata;
  if (typeof rawMetadata === 'string') {
    try {
      metadata = JSON.parse(rawMetadata);
    } catch {
      metadata = rawMetadata;
    }
  }
  const metadataObject = metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata as Record<string, unknown> : {};
  const readString = (...keys: string[]) => {
    for (const key of keys) {
      const direct = (object as Record<string, unknown>)[key];
      if (typeof direct === 'string' && direct.trim()) return direct.trim();
      const metaValue = metadataObject[key];
      if (typeof metaValue === 'string' && metaValue.trim()) return metaValue.trim();
      if (typeof metaValue === 'number' || typeof metaValue === 'boolean') return String(metaValue);
    }
    return null;
  };
  const readNumber = (...keys: string[]) => {
    for (const key of keys) {
      const direct = (object as Record<string, unknown>)[key];
      if (typeof direct === 'number' && Number.isFinite(direct)) return direct;
      if (typeof direct === 'string' && direct.trim() && Number.isFinite(Number(direct))) return Number(direct);
      const metaValue = metadataObject[key];
      if (typeof metaValue === 'number' && Number.isFinite(metaValue)) return metaValue;
      if (typeof metaValue === 'string' && metaValue.trim() && Number.isFinite(Number(metaValue))) return Number(metaValue);
    }
    return null;
  };
  const eventName = object.eventName ?? object.event_name ?? 'unknown';
  const userMessage = readString('userMessage', 'user_message', 'message');
  const technicalMessage = readString('technicalMessage', 'technical_message', 'errorMessage', 'error_message');
  const shortMessage = readString('shortMessage', 'short_message') ?? userMessage ?? technicalMessage ?? eventName;
  return {
    ...object,
    id: object.id ?? crypto.randomUUID(),
    level: object.level ?? 'info',
    source: object.source ?? 'unknown',
    eventName,
    shortMessage,
    userMessage,
    technicalMessage,
    code: readString('code', 'errorCode', 'error_code'),
    status: readNumber('status'),
    method: readString('method'),
    route: readString('route', 'path'),
    durationMs: readNumber('durationMs', 'duration_ms'),
    requestId: readString('requestId', 'request_id'),
    correlationId: readString('correlationId', 'correlation_id'),
    userId: object.userId ?? object.user_id ?? readString('userId', 'user_id'),
    familyId: object.familyId ?? object.family_id ?? readString('familyId', 'family_id'),
    childId: object.childId ?? object.child_id ?? readString('childId', 'child_id'),
    sessionId: object.sessionId ?? object.session_id ?? readString('sessionId', 'session_id'),
    entityType: object.entityType ?? object.entity_type ?? readString('entityType', 'entity_type', 'targetType', 'target_type'),
    entityId: object.entityId ?? object.entity_id ?? readString('entityId', 'entity_id', 'targetId', 'target_id'),
    eventId: object.eventId ?? object.event_id ?? readString('eventId', 'event_id'),
    appVersion: object.appVersion ?? object.app_version ?? readString('appVersion', 'app_version'),
    apiVersion: object.apiVersion ?? object.api_version ?? readString('apiVersion', 'api_version'),
    workerVersion: object.workerVersion ?? object.worker_version ?? readString('workerVersion', 'worker_version'),
    device: object.device ?? readString('device', 'userAgent'),
    platform: object.platform ?? readString('platform', 'origin'),
    metadata,
    createdAt: object.createdAt ?? object.created_at,
  };
}

function normalizeLoggingSettings(payload: unknown, fallback: LoggingSettings = DEFAULT_LOGGING_SETTINGS): LoggingSettings {
  if (isLoggingSettings(payload)) return payload;
  if (payload && typeof payload === 'object' && 'settings' in payload) {
    const settings = (payload as { settings?: unknown }).settings;
    if (isLoggingSettings(settings)) return settings;
  }
  return fallback;
}

function normalizeEnterpriseLogPage(payload: unknown, query: EnterpriseLogQuery | string = ''): EnterpriseLogPage {
  const object = payload && typeof payload === 'object' ? payload as { entries?: unknown[]; logs?: unknown[]; pagination?: Partial<EnterpriseLogPage['pagination']>; generatedAt?: string | null } : {};
  const entries = (object.entries ?? object.logs ?? []).map(normalizeEnterpriseLogEntry);
  const requestedLimit = typeof query === 'object' ? query.limit : undefined;
  const requestedOffset = typeof query === 'object' ? query.offset : undefined;
  const limit = Number(object.pagination?.limit ?? requestedLimit ?? entries.length ?? 50);
  const offset = Number(object.pagination?.offset ?? requestedOffset ?? 0);
  const total = Number(object.pagination?.total ?? entries.length);
  const nextOffset = typeof object.pagination?.nextOffset === 'number' ? object.pagination.nextOffset : (offset + limit < total ? offset + limit : null);
  const previousOffset = typeof object.pagination?.previousOffset === 'number' ? object.pagination.previousOffset : (offset > 0 ? Math.max(offset - limit, 0) : null);
  return {
    entries,
    pagination: {
      limit,
      offset,
      total,
      hasNext: Boolean(object.pagination?.hasNext ?? nextOffset !== null),
      hasPrevious: Boolean(object.pagination?.hasPrevious ?? previousOffset !== null),
      nextOffset,
      previousOffset,
    },
    generatedAt: object.generatedAt ?? null,
  };
}

function logQueryString(query: EnterpriseLogQuery | string = ''): string {
  if (typeof query === 'string') {
    return query.trim() ? `?${query.trim().replace(/^\?/, '')}` : '?limit=50&offset=0';
  }
  const params = new URLSearchParams();
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  if (query.level) params.set('level', query.level);
  if (query.source) params.set('source', query.source);
  if (query.eventName) params.set('eventName', query.eventName);
  if (query.search) params.set('q', query.search);
  if (query.since) params.set('since', query.since);
  if (query.until) params.set('until', query.until);
  if (query.includeArchived) params.set('includeArchived', 'true');
  if (query.archivedOnly) params.set('archivedOnly', 'true');
  return `?${params.toString()}`;
}

export function getStoredAccessToken(): string | null {
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredAccessToken(token: string | null): void {
  if (token) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredAccessToken();
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12000);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers,
      credentials: 'include',
      signal: init.signal ?? controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new SiktraApiError('Die API hat nicht rechtzeitig geantwortet.', 408, 'API_TIMEOUT');
    }
    throw new SiktraApiError('Die API ist aktuell nicht erreichbar.', 0, 'API_NETWORK_ERROR');
  } finally {
    window.clearTimeout(timeout);
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  const payload = contentType.includes('application/json') ? await response.json().catch(() => null) : await response.text().catch(() => null);

  if (!response.ok) {
    const error = extractError(payload, `API Fehler ${response.status}`);
    throw new SiktraApiError(error.message, response.status, error.code);
  }

  return unwrap<T>(payload);
}

function persistSessionToken(session?: SessionInfo | null): void {
  const token = session?.accessToken ?? session?.clientToken ?? null;
  if (token) setStoredAccessToken(token);
}

export const api = {
  baseUrl: API_BASE_URL,

  async login(email: string, password: string): Promise<AuthState> {
    const data = await request<{ user: AuthState['user']; session?: SessionInfo }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    persistSessionToken(data.session);
    return { authenticated: true, user: data.user, session: data.session ?? null };
  },

  async me(): Promise<AuthState> {
    const data = await request<{ authenticated?: boolean; user?: AuthState['user']; session?: SessionInfo }>('/auth/me');
    return { authenticated: Boolean(data.authenticated ?? data.user), user: data.user ?? null, session: data.session ?? null };
  },

  async logout(): Promise<void> {
    try {
      await request('/auth/logout', { method: 'POST' });
    } finally {
      setStoredAccessToken(null);
    }
  },

  async health(): Promise<HealthStatus> {
    return request<HealthStatus>('/health');
  },

  async adminUsers(query = ''): Promise<AdminUserRow[]> {
    const suffix = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
    const data = await request<{ users?: AdminUserRow[]; results?: AdminUserRow[] }>(`/admin/users${suffix}`);
    return data.users ?? data.results ?? [];
  },

  async createAdminUser(input: AdminUserCreateInput): Promise<{ generatedPassword?: string | null }> {
    return request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async updateAdminUser(userId: string, input: AdminUserUpdateInput): Promise<unknown> {
    return request(`/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  async deleteAdminUser(userId: string): Promise<unknown> {
    return request(`/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  },

  async resetAdminUserPassword(userId: string): Promise<AdminPasswordResetResult> {
    return request(`/admin/users/${encodeURIComponent(userId)}/password-reset`, { method: 'POST' });
  },

  async auditLog(): Promise<AuditEntry[]> {
    const data = await request<{ entries?: AuditEntry[]; auditLogs?: AuditEntry[] }>('/admin/audit-log');
    return data.entries ?? data.auditLogs ?? [];
  },

  async adminFamilyMembers(familyId: string): Promise<FamilyMemberSummary[]> {
    const data = await request<{ members?: FamilyMemberSummary[] }>(`/admin/families/${encodeURIComponent(familyId)}/members`);
    return data.members ?? [];
  },

  async updateFamilyMemberRole(input: { familyId: string; memberId: string; role: FamilyMemberRole; reason: string }): Promise<unknown> {
    return request(`/admin/families/${encodeURIComponent(input.familyId)}/members/${encodeURIComponent(input.memberId)}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role: input.role, reason: input.reason }),
    });
  },

  async updateFamilyLicense(input: {
    familyId: string;
    licenseStatus: string;
    trialEnd?: string;
    licenseExpiresAt?: string;
    note: string;
  }): Promise<unknown> {
    return request(`/admin/families/${encodeURIComponent(input.familyId)}/license`, {
      method: 'PATCH',
      body: JSON.stringify({
        licenseStatus: input.licenseStatus,
        trialEnd: input.trialEnd || undefined,
        licenseExpiresAt: input.licenseExpiresAt || undefined,
        reason: input.note,
      }),
    });
  },

  async platformLoggingSettings(): Promise<LoggingSettings> {
    const data = await request<unknown>('/platform/logging/settings');
    return normalizeLoggingSettings(data);
  },

  async updatePlatformLoggingSettings(settings: LoggingSettings): Promise<LoggingSettings> {
    const data = await request<unknown>('/platform/logging/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
    return normalizeLoggingSettings(data, settings);
  },

  async enterpriseLogs(query: EnterpriseLogQuery | string = ''): Promise<EnterpriseLogPage> {
    const data = await request<unknown>(`/platform/logs${logQueryString(query)}`);
    return normalizeEnterpriseLogPage(data, query);
  },

  async archiveEnterpriseLogs(ids: string[], reason = 'In Siktra Manage archiviert'): Promise<{ ok?: boolean; archivedCount?: number; archivedAt?: string }> {
    return request('/platform/logs/archive', {
      method: 'POST',
      body: JSON.stringify({ ids, reason }),
    });
  },

  async archiveEnterpriseLogsByFilter(filters: EnterpriseLogQuery, reason = 'Alle gefilterten Logs in Siktra Manage archiviert'): Promise<{ ok?: boolean; archivedCount?: number; archivedAt?: string }> {
    return request('/platform/logs/archive-all', {
      method: 'POST',
      body: JSON.stringify({ filters: { ...filters, offset: 0 }, reason }),
    });
  },

  async enterpriseLogsCsv(query: EnterpriseLogQuery | string = ''): Promise<string> {
    const suffix = logQueryString(typeof query === 'string' ? query : { ...query, limit: 1000, offset: 0 });
    return request<string>(`/platform/logs/download${suffix}`, {
      headers: { Accept: 'text/csv' },
    });
  },

  async websitePayload(): Promise<WebsitePayload> {
    const [overview, pages, assets, navigation, settings] = await Promise.all([
      request<{ pageCount?: number; publishedCount?: number; draftCount?: number; assetCount?: number; settings?: WebsiteSettings; recentPages?: WebsitePageSummary[]; generatedAt?: string }>('/admin/website/overview').catch(() => null),
      request<{ pages?: WebsitePageSummary[] }>('/admin/website/pages').catch(() => ({ pages: [] })),
      request<{ assets?: WebsiteAsset[] }>('/admin/website/assets').catch(() => ({ assets: [] })),
      request<{ navigation?: WebsiteNavigationItem[] }>('/admin/website/navigation').catch(() => ({ navigation: [] })),
      request<{ settings?: WebsiteSettings }>('/admin/website/settings').catch(() => ({ settings: { siteName: 'Siktra Sleep' } as WebsiteSettings })),
    ]);
    return {
      overview: overview as WebsiteOverview | null,
      pages: pages.pages ?? [],
      assets: assets.assets ?? [],
      navigation: navigation.navigation ?? [],
      settings: settings.settings ?? overview?.settings ?? { siteName: 'Siktra Sleep' },
    };
  },

  async websitePage(pageId: string): Promise<WebsitePage> {
    const data = await request<{ page?: WebsitePage }>(`/admin/website/pages/${encodeURIComponent(pageId)}`);
    if (!data.page) throw new SiktraApiError('Website-Seite wurde nicht gefunden.', 404, 'NOT_FOUND');
    return data.page;
  },

  async createWebsitePage(input: { title: string; slug: string; seoTitle?: string | null; seoDescription?: string | null; ogImageAssetId?: string | null; sections: unknown[] }): Promise<WebsitePage> {
    const data = await request<{ page?: WebsitePage }>('/admin/website/pages', { method: 'POST', body: JSON.stringify(input) });
    if (!data.page) throw new SiktraApiError('Website-Seite konnte nicht angelegt werden.', 500, 'WEBSITE_PAGE_MISSING');
    return data.page;
  },

  async updateWebsitePage(pageId: string, input: { title: string; slug: string; seoTitle?: string | null; seoDescription?: string | null; ogImageAssetId?: string | null; sections: unknown[] }): Promise<WebsitePage> {
    const data = await request<{ page?: WebsitePage }>(`/admin/website/pages/${encodeURIComponent(pageId)}`, { method: 'PATCH', body: JSON.stringify(input) });
    if (!data.page) throw new SiktraApiError('Website-Seite konnte nicht gespeichert werden.', 500, 'WEBSITE_PAGE_MISSING');
    return data.page;
  },

  async publishWebsitePage(pageId: string): Promise<WebsitePage> {
    const data = await request<{ page?: WebsitePage }>(`/admin/website/pages/${encodeURIComponent(pageId)}/publish`, { method: 'POST' });
    if (!data.page) throw new SiktraApiError('Website-Seite konnte nicht veröffentlicht werden.', 500, 'WEBSITE_PAGE_MISSING');
    return data.page;
  },

  async unpublishWebsitePage(pageId: string): Promise<WebsitePage> {
    const data = await request<{ page?: WebsitePage }>(`/admin/website/pages/${encodeURIComponent(pageId)}/unpublish`, { method: 'POST' });
    if (!data.page) throw new SiktraApiError('Website-Seite konnte nicht zurückgezogen werden.', 500, 'WEBSITE_PAGE_MISSING');
    return data.page;
  },

  async duplicateWebsitePage(pageId: string): Promise<WebsitePage> {
    const data = await request<{ page?: WebsitePage }>(`/admin/website/pages/${encodeURIComponent(pageId)}/duplicate`, { method: 'POST' });
    if (!data.page) throw new SiktraApiError('Website-Seite konnte nicht dupliziert werden.', 500, 'WEBSITE_PAGE_MISSING');
    return data.page;
  },

  async deleteWebsitePage(pageId: string): Promise<unknown> {
    return request(`/admin/website/pages/${encodeURIComponent(pageId)}`, { method: 'DELETE' });
  },

  async createWebsiteAsset(input: { title: string; altText?: string; url: string; sourceType?: string; mimeType?: string | null; width?: number | null; height?: number | null }): Promise<WebsiteAsset> {
    const data = await request<{ asset?: WebsiteAsset }>('/admin/website/assets', { method: 'POST', body: JSON.stringify(input) });
    if (!data.asset) throw new SiktraApiError('Asset konnte nicht angelegt werden.', 500, 'WEBSITE_ASSET_MISSING');
    return data.asset;
  },

  async updateWebsiteAsset(assetId: string, input: { title: string; altText?: string; url: string; sourceType?: string; mimeType?: string | null; width?: number | null; height?: number | null }): Promise<WebsiteAsset> {
    const data = await request<{ asset?: WebsiteAsset }>(`/admin/website/assets/${encodeURIComponent(assetId)}`, { method: 'PATCH', body: JSON.stringify(input) });
    if (!data.asset) throw new SiktraApiError('Asset konnte nicht gespeichert werden.', 500, 'WEBSITE_ASSET_MISSING');
    return data.asset;
  },

  async deleteWebsiteAsset(assetId: string): Promise<unknown> {
    return request(`/admin/website/assets/${encodeURIComponent(assetId)}`, { method: 'DELETE' });
  },

  async updateWebsiteNavigation(items: WebsiteNavigationItem[]): Promise<WebsiteNavigationItem[]> {
    const data = await request<{ navigation?: WebsiteNavigationItem[] }>('/admin/website/navigation', { method: 'PUT', body: JSON.stringify({ items }) });
    return data.navigation ?? [];
  },

  async updateWebsiteSettings(settings: Partial<WebsiteSettings>): Promise<WebsiteSettings> {
    const data = await request<{ settings?: WebsiteSettings }>('/admin/website/settings', { method: 'PATCH', body: JSON.stringify(settings) });
    return data.settings ?? { siteName: 'Siktra Sleep' };
  },

};
